import React from 'react';
import { PropTypes } from 'prop-types';
import { get } from 'lodash';
import { useFind } from 'api/APIHooks';
import { Card } from 'common/Card';
import CardSubHeader from 'common/Card/CardSubHeader';
import { useFeatureFlag } from 'common/hooks';
import { useCurrentProviderId } from 'common/contexts/CurrentProviderContext';
import { useAssigneeOptions, REQUEST_UNASSIGNED } from 'common/Workqueue/hooks/useAssigneeOptions';
import { ReviewReasonDetailsCard } from 'common/Workqueue/ReviewReasonDetailsCard';
import ClientDetailsSection from './ClientDetailsSection';
import EnrollmentDetailsCardHeader from './EnrollmentDetailsCardHeader';
import RequestDetailsSection from './RequestDetailsSection';
import SocialCareCoverageDetailsSection from './SocialCareCoverageDetailsSection';
import AssessmentFormsSection from './AssessmentFormsSection';
import SupportingDocumentsSection from './SupportingDocumentsSection';
import { REQUEST_TYPES, ENROLLMENT_STATUSES } from '../../constants';
import { useFindEligibilityForInsurance } from '../../hooks/useFindEligibilityForInsurance';
import { NcTracksVerification } from '../NcTracks/NcTracksVerification';
import { useEnrollmentReviewReasonOptions } from '../../hooks/useEnrollmentReviewReasonOptions';
import { useReviewEnrollmentRequest } from '../../hooks/useUpdateEnrollmentRequest';
import EnrollmentTimelineEventsCard from '../EnrollmentTimelineEventsCard';

const EnrollmentDetailsCard = ({
  enrollmentRequest,
  primaryInsurance,
  onClientNameClick,
}) => {
  const pays6096UpdateEWQDesign = useFeatureFlag('pays-6096-update-ewq-design');
  const allowUserToMarkEnrollmentRequestInReview = useFeatureFlag('allow-user-to-mark-enrollment-request-in-review');
  const enrollmentTimelineHistoryEnabled = useFeatureFlag('pays-9175-ewq-show-in-review-history-section');

  const planId = enrollmentRequest?.plan?.id;
  const reviewReasonOptions = useEnrollmentReviewReasonOptions(planId) || [];
  const assigneeOptions = useAssigneeOptions('enrollments');
  const reviewEnrollmentRequest = useReviewEnrollmentRequest(false);
  const isReviewStatus = enrollmentRequest.enrollment_status === ENROLLMENT_STATUSES.inReview;
  const showReviewEnrollmentRequest = isReviewStatus && allowUserToMarkEnrollmentRequestInReview;

  const handleSubmitReviewUpdate = async (formData) => {
    await reviewEnrollmentRequest(
      enrollmentRequest,
      formData.review_reason,
      formData.in_review_note,
      formData.assignee === REQUEST_UNASSIGNED || !formData.assignee ? null : formData.assignee,
    );
  };

  const reviewReason = enrollmentRequest.enrollment_review_reason;
  const assignee = enrollmentRequest.assignee;
  const inReviewNote = enrollmentRequest.in_review_note;

  const fetchEligibilityRecord = !!primaryInsurance?.id;
  const eligibility = useFindEligibilityForInsurance(primaryInsurance, fetchEligibilityRecord);

  const showNcTracksVerification = !!eligibility?.id;

  const isDisenroll = enrollmentRequest.request_type === REQUEST_TYPES.disenroll;
  const providerId = useCurrentProviderId();
  const { data: clientRelationshipData } = useFind(
    'client_relationship',
    {
      person: enrollmentRequest?.person?.id,
      provider: providerId,
    },
    {
      queryConfig: {
        enabled: !!enrollmentRequest?.person?.id && !!providerId && pays6096UpdateEWQDesign,
        placeholderData: undefined,
      },
    },
  );

  const clientRelationships = get(clientRelationshipData, 'data.data', []);

  return (
    <div className="pt-4 flex-1 px-3 py-2 space-y-6">
      {showReviewEnrollmentRequest && (
        <ReviewReasonDetailsCard
          reviewReason={reviewReason}
          inReviewNote={inReviewNote}
          assignee={assignee}
          onSubmit={handleSubmitReviewUpdate}
          assigneeOptions={assigneeOptions}
          reviewReasonOptions={reviewReasonOptions}
        />
      )}
      <Card className="mt-2 px-4 py-2 bg-white">
        <div className="p-4">
          <EnrollmentDetailsCardHeader
            enrollmentRequest={enrollmentRequest}
            label={isDisenroll ?
              'Disenrollment Request #' :
              'Enrollment Request #'}
          />
        </div>
        <hr className="pt-2 -mx-4 border-dark-border-blue" />
        <div className="grid grid-cols-2 divide-x divide-solid divide-dark-border-blue">
          <div className="inline-block py-4 space-y-4">
            <ClientDetailsSection
              showClientFacesheetLink={clientRelationships.length > 0}
              enrollmentRequest={enrollmentRequest}
              primaryInsurance={primaryInsurance}
              onClientNameClick={onClientNameClick}
            />
          </div>
          <div className="inline-block px-4 py-4 m-2 space-y-4">
            <RequestDetailsSection enrollmentRequest={enrollmentRequest} isDisenroll={isDisenroll} />
          </div>
        </div>
        <hr className="pt-2 -mx-4 border-dark-border-blue" />
        {
          showNcTracksVerification ? (
            <div className="grid grid-cols-2 divide-x divide-solid divide-dark-border-blue">
              <div className="inline-block py-4 space-y-4">
                <CardSubHeader>Insurance Verification</CardSubHeader>
                <NcTracksVerification eligibility={eligibility} />
              </div>

              <div className="inline-block px-4 py-4 m-2 space-y-4">
                <SocialCareCoverageDetailsSection enrollmentRequest={enrollmentRequest} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 divide-x divide-solid divide-dark-border-blue">
              <div className="inline-block py-4 space-y-4">
                <SocialCareCoverageDetailsSection enrollmentRequest={enrollmentRequest} />
              </div>
            </div>
          )
        }
      </Card>
      <div>
        <AssessmentFormsSection enrollmentRequest={enrollmentRequest} />
      </div>
      <div>
        <SupportingDocumentsSection enrollmentRequest={enrollmentRequest} />
      </div>
      {enrollmentTimelineHistoryEnabled && (
        <EnrollmentTimelineEventsCard enrollmentRequestId={enrollmentRequest.id} />
      )}
    </div>

  );
};

EnrollmentDetailsCard.propTypes = {
  enrollmentRequest: PropTypes.object.isRequired,
  primaryInsurance: PropTypes.object.isRequired,
  onClientNameClick: PropTypes.func,
};

EnrollmentDetailsCard.defaultProps = {
  onClientNameClick: null,
};

export default EnrollmentDetailsCard;
